import { ReactNode } from 'react';

import {
  Box,
  Stack,
  SxProps,
  Theme,
  Typography,
  useTheme
} from '@mui/material';
import { Link } from 'react-router-dom';

import { FavoriteHeartButton } from '@/shared/ui/components/button/FavoriteHeart';
import {
  buildRowQuery,
  getRowSurfaceSx,
  IRowField,
  ListRowFrame
} from '@/shared/ui/components/list-row/ListRowFrame';

import { pickImageUrl } from '@/shared/lib/images';
import { getAgeDeclension } from '@/shared/lib/plural';
import { passedSx } from '@/shared/lib/utils';

import { ISelectValue } from '@/shared/types/select';

import { UserAvatarWithPlaceholder } from '@/entities/user';

import { CardNameLine } from './CardNameLine';
import { ROW_NAME_SINGLE_LINE_SX, ROW_VALUE_SX } from './cardRowStyles';
import { IUserCardBase, IUserCardViewProps } from './CardTypes';
import { useUserCardActions } from './useUserCardActions';

/** Вид строки: развёрнутая или компактная */
type TRowVariant = 'list' | 'compact';

/**
 * Обёртка строки: поверхность (фон/рамка/радиус/hover) — общий с остальными
 * строчными видами токен `getRowSurfaceSx` (18976). Раньше её нёс
 * собственный `<Card>` этого домена — единственный из четырёх, у которого
 * поверхность вообще была. Обёртка остаётся ЗДЕСЬ, а не в самом `ListRowFrame`
 * (`surface={false}` на нём ниже): звезда избранного стоит вне каркаса и ей
 * нужна ОДНА рамка на строку целиком, а не рамка вокруг одного каркаса и
 * голый хвост под ней. `bottomContent` теперь передаётся каркасу слотом
 * `footer` (см. ниже) — обёртка его не рендерит.
 */
const ROW_WRAPPER_SX: SxProps<Theme> = { position: 'relative', width: '100%' };

/**
 * Тач-цель одной кнопки корневого угла (см. `UserCardCornerActions`,
 * `gap: 0` — цели стоят вплотную).
 */
const CORNER_ACTION_HIT_AREA = 44;

/**
 * Диаметр `StatusDot` (18976, Р77) плюс зазор перед ним в угловой группе —
 * учитывается в резерве (`getActionsReserveSx`) и в отступе самого дота
 * (`getStatusDotSx`) ТОЛЬКО на широкой ветке `compact`, где дот действительно
 * стоит в углу; на узкой он скрыт (`display: none`), и резервировать под него
 * место незачем.
 */
const CORNER_DOT_RESERVE = 16 + 8;

/**
 * Невидимый распорок ТОЙ ЖЕ ширины, что фактическая угловая группа
 * (18976, Р3, второй заход; Р77 — учёт дота статуса). Настоящие кнопки
 * остаются, как и были, ВНЕ ссылки на профиль — ФИКСИРОВАННЫМ верхним правым
 * углом строки каркаса (`trailing`, см. `buildTrailing` ниже), нарочно, чтобы
 * не вкладывать интерактивные элементы в `<a>` (та же причина, по которой
 * `footer`/`bottomContent` держится СНАРУЖИ ссылки, см. JSDoc `wrapWithLink`) —
 * вложенная кнопка внутри `<a>` невалидна по HTML5 и ведёт себя
 * непредсказуемо по фокусу/клику.
 *
 * Раньше группа была ЧИСТЫМ оверлеем: не резервировала ширину нигде, поэтому
 * длинное имя тянулось на всю ширину колонки и уезжало буквами ПОД иконки
 * вместо обрезки многоточием (воспроизведено на ФИО с дефисами на 375/900).
 * Распорок передаётся каркасу слотом `titleActions` — РЕАЛЬНЫМ флекс-соседом
 * СТРОКИ ЗАГОЛОВКА (см. `ListRowFrame`): он не кликабелен и не виден, только
 * занимает место, которое трейлинг потом визуально накрывает собой в ТОЧНО той
 * же позиции (каркасный `getCornerSx`, отсчитанный от той же обёртки строки).
 *
 * `hasDot` (18976, Р77) — растит резерв ТОЛЬКО на широкой ветке (`ACTOR_ROW_WIDE`):
 * там дот реально стоит в угловой группе; на узкой он скрыт, и растить резерв
 * незачем — имени осталось бы меньше места без пользы.
 * @param hasFolder - есть ли в группе кнопка «в папку» (тогда распорок вдвое шире)
 * @param hasDot - есть ли в группе плотный индикатор статуса (только `compact`)
 */
const getActionsReserveSx = (
  hasFolder: boolean,
  hasDot: boolean
): SxProps<Theme> => {
  const base = hasFolder ? CORNER_ACTION_HIT_AREA * 2 : CORNER_ACTION_HIT_AREA;

  if (!hasDot) return { width: base };

  return {
    width: base,
    [ACTOR_ROW_WIDE]: { width: base + CORNER_DOT_RESERVE }
  };
};

/**
 * ДОМЕННЫЙ порог «широкой» ветки актёрской строки. Каркас (`ListRowFrame`) прямо
 * требует строить свой порог этим билдером, а не подставлять его ВНУТРЕННИЙ
 * `WIDE_ROW` (420px, см. JSDoc `ListRowFrame.tsx`): числа совпадают историческим
 * совпадением (актёрская строка держала 420px ещё до каркаса), а не потому что
 * домен обязан жить на пороге каркаса — сдвинь каркас свой внутренний порог, и
 * без своей константы разметка актёра уехала бы следом без единой правки здесь.
 */
const ACTOR_ROW_WIDE = buildRowQuery(420);

/**
 * Плашка статуса компактного вида (`statusSlot`, слот `badges` каркаса) скрыта
 * на широкой ветке (18976, Р77): там статус несёт дот в угловой группе (см.
 * `getStatusDotSx`), а показывать и то и другое разом — разнобой. Применяется
 * ТОЛЬКО когда у строки есть дот-альтернатива (`hasDenseStatus`) — у
 * «Пользователь удалён» дота не бывает вовсе (см. `ResponseStatusBlock`), и
 * плашка остаётся видимой на любой ширине без этого правила.
 */
const COMPACT_STATUS_PILL_SX: SxProps<Theme> = {
  [ACTOR_ROW_WIDE]: { display: 'none' }
};

/**
 * Плотный индикатор статуса в угловой группе `compact` (18976, Р77) — обратное
 * правило `COMPACT_STATUS_PILL_SX`: виден ТОЛЬКО на широкой ветке. На узкой
 * (375–900px) наведения нет, а у `pending` контраст глифа ниже порога — статус
 * там обязан быть словами, а не кружком (см. `modules/status-rendering.md`).
 */
const COMPACT_DOT_SX: SxProps<Theme> = {
  display: 'none',
  [ACTOR_ROW_WIDE]: { display: 'flex' }
};

/**
 * Обёртка пары решения в `decisionSlot` каркаса (18976, Р77) — тот же приём,
 * что несёт `CastingListRow#DECISION_SLOT_WRAP_SX`: без явной ширины проценты
 * кнопок внутри (`UsersResponseActions`, 50%/50%) не могут разрешиться — на
 * широкой ветке `list` элемент лежит в потоке ряда полей и получил бы ширину
 * по контенту, на `compact` — вовсе абсолютный угол без исходной ширины.
 */
const DECISION_SLOT_WRAP_SX: SxProps<Theme> = {
  width: '100%',
  [ACTOR_ROW_WIDE]: { maxWidth: 360 }
};

/**
 * Трейлинг строки: дот статуса (только `compact`, широкая ветка) + папка +
 * сердце — ОДНА группа, которую каркас (`ListRowFrame`) кладёт ФИКСИРОВАННЫМ
 * верхним правым углом строки в ОБОИХ видах (`trailing`, `getCornerSx`) — тот
 * же приём, что уже несёт `CastingListRow#buildTrailing`.
 *
 * До этого захода группа была ОТДЕЛЬНЫМ оверлеем СНАРУЖИ каркаса
 * (`UserCardCornerActions`, свой `position: absolute` поверх строки, по
 * центру в `compact`) — заказчик 17.09.2026 прямо потребовал того же
 * механизма, что у кастинга: «сердце и папку ещё не переносили [в
 * фиксированный угол]», один каркас на все четыре домена. Побочный эффект
 * миграции: дот статуса (`[ACTOR_ROW_WIDE]`) раньше стоял СНАРУЖИ контейнера,
 * который каркас объявляет на себе, и `@container` для него не резолвился НИ
 * НА КАКОЙ ширине (замечено живым замером в браузере) — `trailing` каркаса
 * рендерится ВНУТРИ его собственного контейнерного узла, и дефект снимается
 * самой миграцией, без отдельного контейнерного объявления домена.
 *
 * `gap: 0` — тач-цели папки/сердца стоят вплотную (см. прежний
 * `UserCardCornerActions`, тот же приём); у дота свой отступ (`mr`), он не
 * тач-цель и стоит отдельным элементом ряда.
 * @param hasCompactDot - есть ли дот в группе (`compact`, широкая ветка,
 * см. `hasCompactDot` в компоненте)
 * @param denseStatusSlot - плотный индикатор статуса; вызывается, только когда
 * `hasCompactDot` истинен
 * @param folder - слот кнопки «в папку»; `undefined`, если экран его не заполнил
 * @param heart - кнопка избранного (сердце)
 */
const buildTrailing = (
  hasCompactDot: boolean,
  denseStatusSlot: (() => ReactNode) | undefined,
  folder: ReactNode,
  heart: ReactNode
) => (
  <Stack direction='row' alignItems='center' gap={0}>
    {hasCompactDot && denseStatusSlot && (
      <Box sx={[COMPACT_DOT_SX, { mr: 2 }]}>{denseStatusSlot()}</Box>
    )}
    {folder}
    {heart}
  </Stack>
);

/** Имя компактного ряда: одна строка с эллипсисом, крупнее в широкой колонке */
const COMPACT_NAME_SX: SxProps<Theme> = {
  ...ROW_NAME_SINGLE_LINE_SX,
  [ACTOR_ROW_WIDE]: { fontSize: '15px' }
};

/**
 * Точка-разделитель метаданных компактного ряда. Ставится ВМЕСТЕ со своим полем и
 * только с ним: одинокая точка после имени читалась бы как обрезанный текст.
 */
const DOT_SX: SxProps<Theme> = { color: 'text.disabled', flexShrink: 0 };

/**
 * Возраст компактного ряда. `flexShrink: 0` — возраст короткий и осмыслен только
 * целиком: сжатый флексом, он превращался в «35 …», то есть в мусор на месте
 * единственного числа ряда. Ужимается вместо него имя, которому есть что терять.
 */
const COMPACT_AGE_SX: SxProps<Theme> = {
  ...ROW_VALUE_SX,
  whiteSpace: 'nowrap',
  flexShrink: 0
};

/** Метаданные, которые компактный ряд показывает только в широкой колонке */
const COMPACT_EXTRA_SX: SxProps<Theme> = {
  display: 'none',
  minWidth: 0,
  alignItems: 'baseline',
  gap: 2,
  [ACTOR_ROW_WIDE]: { display: 'flex' }
};

/** Одна строка метаданных компактного ряда обрезается по краю колонки */
const COMPACT_VALUE_SX: SxProps<Theme> = {
  ...ROW_VALUE_SX,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis'
};

/**
 * Названия справочных значений одной строкой.
 *
 * Экспортируется: тем же способом склеивают города и типы проекта полоса полей
 * крупной карточки (`Card.tsx`) и шапка ленты превью (`CardPhotoStrip.tsx`) — а
 * вторая копия «join через запятую» разъехалась бы с этой при первой правке
 * разделителя.
 * @param values - значения справочника из списочного ответа
 */
export const joinNames = (values?: ISelectValue[] | null) =>
  (values ?? []).map(value => value.name).join(', ');

/**
 * Возраст с корректным склонением или пустая строка, если возраст не заполнен
 * @param age - возраст актёра из списочного ответа
 */
const formatAge = (age: number | null) => (age ? getAgeDeclension(age) : '');

/**
 * Три поля строки `list` в фиксированном порядке — каркас печатает пустую форму
 * сам для незаполненных, ряды соседних актёров совпадают по высоте. Все три
 * подписи мужского рода — «Не указан», а не каркасный дефолт среднего рода
 * (18976, Р6).
 * @param user - данные актёра
 */
const buildListFields = (user: IUserCardBase): IRowField[] => [
  { label: 'Возраст', value: formatAge(user.age), emptyValue: 'Не указан' },
  {
    label: (user.cities?.length ?? 0) > 1 ? 'Города' : 'Город',
    value: joinNames(user.cities),
    emptyValue: 'Не указан'
  },
  {
    label: 'Опыт работы',
    value: joinNames(user.job_project_types),
    emptyValue: 'Не указан'
  }
];

/**
 * Строка метаданных компактного вида: возраст + доп. текст через «·». Собирается
 * доменом целиком и передаётся каркасу единым узлом (`meta`) — типографика
 * разделителя и правило «точка идёт только вместе со своим полем» здесь же.
 * @param user - данные актёра
 * @param additionalInfo - дополнительная информация (например, название кастинга)
 */
const buildCompactMeta = (user: IUserCardBase, additionalInfo?: string) => {
  const age = formatAge(user.age);
  const extras = [joinNames(user.cities), additionalInfo].filter(Boolean);

  return (
    <>
      {!!age && (
        <>
          <Typography sx={DOT_SX}>·</Typography>
          <Typography sx={COMPACT_AGE_SX}>{age}</Typography>
        </>
      )}
      {extras.map(text => (
        <Stack key={text} direction='row' sx={COMPACT_EXTRA_SX}>
          <Typography sx={DOT_SX}>·</Typography>
          <Typography sx={COMPACT_VALUE_SX}>{text}</Typography>
        </Stack>
      ))}
    </>
  );
};

/**
 * Динамический контент `ListRowFrame`, зависящий от вида строки: `list` несёт
 * полное имя, доп. информацию подзаголовком и три поля; `compact` — имя в одну
 * строку, приписку подзаголовком и мета-строку вместо полей. Вынесено из
 * `CardListRow` отдельной функцией — та же причина, что у
 * `CastingListRow#getFrameContent`: набор `isCompact`-ветвлений внутри самого
 * компонента разгонял его цикломатическую сложность выше порога линтера.
 * @param user - данные актёра
 * @param isCompact - плотный вид (`compact`)
 * @param additionalInfo - дополнительная информация (например, название кастинга)
 * @param metaSuffix - приглушённая приписка компактного ряда
 */
const getFrameContent = <T extends IUserCardBase>(
  user: T,
  isCompact: boolean,
  additionalInfo?: string,
  metaSuffix?: string
) => ({
  title: isCompact ? (
    <CardNameLine
      firstName={user.first_name}
      lastName={user.last_name}
      isVerified={user.system_photo_attached}
      sx={COMPACT_NAME_SX}
    />
  ) : (
    <CardNameLine
      firstName={user.first_name}
      lastName={user.last_name}
      isVerified={user.system_photo_attached}
    />
  ),
  subtitle: isCompact ? metaSuffix : additionalInfo,
  fields: isCompact ? undefined : buildListFields(user),
  meta: isCompact ? buildCompactMeta(user, additionalInfo) : undefined
});

/**
 * Плашка статуса естественной ширины (18976, Р77) слотом `badges` каркаса.
 * Гейт — булев `hasStatusContent` от экрана, а не truthy результата вызова
 * `statusSlot()`: слот строит React-элемент `<ResponseStatusBlock .../>`
 * синхронно и truthy сам по себе независимо от того, что сам компонент решит
 * вернуть при рендере (`null`) — проверка результата вызова заводила бы
 * пустую обёртку `badges` (и лишний зазор `RowBody`) даже там, где статусу
 * нечего показать (найдено живым замером: пара решения давала пустой `badges`
 * рядом с самими кнопками). На `compact`-широкой ветке, где статус несёт дот в
 * угловой группе, плашка скрывается классом `COMPACT_STATUS_PILL_SX` вместо
 * того чтобы не рендериться вовсе. Вынесено из `CardListRow` отдельной
 * функцией — набор ветвлений внутри самого компонента разгонял его
 * цикломатическую сложность выше порога линтера.
 * @param hasStatusContent - есть ли у `statusSlot` реальное содержимое
 * @param isCompact - плотный вид (`compact`)
 * @param hasCompactDot - есть ли в угловой группе дот статуса (`compact`,
 * широкая ветка)
 * @param statusSlot - плашка статуса; вызывается, только когда
 * `hasStatusContent` истинен
 */
const buildStatusBadges = (
  hasStatusContent: boolean,
  isCompact: boolean,
  hasCompactDot: boolean,
  statusSlot: (() => ReactNode) | undefined
) => {
  if (!hasStatusContent) return false;

  return (
    <Box sx={isCompact && hasCompactDot ? COMPACT_STATUS_PILL_SX : undefined}>
      {statusSlot?.()}
    </Box>
  );
};

/**
 * Пара решения (18976, Р77) слотом `decisionSlot` каркаса — тот же приём, что
 * `buildStatusBadges`: гейт булевым `hasDecisionContent` от экрана, а не
 * truthy результата вызова слота (без гейта каркас получал бы `decisionSlot`
 * на каждой строке, где экран вообще передал проп, и `compact` бампал высоту
 * даже там, где решать нечего). Вынесено отдельной функцией по той же
 * причине, что у `buildStatusBadges`.
 * @param hasDecisionContent - есть ли у `decisionSlot` реальное содержимое
 * @param decisionSlot - пара решения; вызывается, только когда
 * `hasDecisionContent` истинен
 */
const buildDecisionContent = (
  hasDecisionContent: boolean,
  decisionSlot: (() => ReactNode) | undefined
) => {
  if (!hasDecisionContent) return false;

  return <Box sx={DECISION_SLOT_WRAP_SX}>{decisionSlot?.()}</Box>;
};

/**
 * Нижняя полоса `bottomContent` каркаса строки — вызывается один раз и
 * проверяется по результату (не функции), потому что здесь домен НЕ получает
 * готового булева флага от экрана (в отличие от статуса/решения выше):
 * `bottomContent` остаётся универсальным слотом произвольного содержимого, а
 * не типизированным контрактом статуса отклика. Вынесено отдельной функцией
 * по той же причине, что у `buildStatusBadges`/`buildDecisionContent`.
 * @param bottomContent - слот действий/статуса строки (отклики, приглашения);
 * `undefined`, если экрану нечего показать
 */
const buildRowFooter = (bottomContent?: () => ReactNode) => {
  const result = bottomContent?.();
  if (!result) return undefined;

  return (
    <Box sx={{ px: 2, pb: 2, [ACTOR_ROW_WIDE]: { px: 3, pb: 3 } }}>
      {result}
    </Box>
  );
};

interface IProps<T extends IUserCardBase> extends IUserCardViewProps<T> {
  variant: TRowVariant;
  metaSuffix?: string;
}

/**
 * Строчные виды карточки актёра: `list` (фото + три подписанных поля) и `compact`
 * (одна строка минимальной высоты). Геометрию несёт `ListRowFrame`; здесь —
 * только доменное содержимое (какие поля, как собрать метастроку).
 * @param props - пропсы
 * @param props.user - данные актёра из списочного ответа
 * @param props.variant - `list` или `compact`
 * @param props.sx - стили карточки
 * @param props.link - ссылка перехода, если экрану нужна не карточка профиля
 * @param props.bottomContent - слот действий/статуса (отклики, приглашения);
 * передаётся каркасу слотом `footer` — ВНУТРИ его контейнерного контекста, но
 * СНАРУЖИ ссылки на профиль (18976, второй заход: раньше стоял соседом
 * `ListRowFrame` и никогда не матчил свой широкий порог)
 * @param props.additionalInfo - дополнительная информация (например, название кастинга)
 * @param props.metaSuffix - приглушённая приписка под метаданными компактного ряда
 * @param props.renderFolderAction - слот кнопки «в папку», рисуется рядом с сердцем
 * @param props.onBookmarked - снекбар «добавлен в избранное»
 * @param props.onUnbookmarked - снекбар «убрано из избранного»
 * @param props.statusSlot - плашка статуса естественной ширины (18976, Р77):
 * `badges` каркаса — под именем в `list`, узкая ветка `compact` (широкая
 * отдаёт статус дот`у, см. `denseStatusSlot`)
 * @param props.hasStatusContent - есть ли у `statusSlot` реальное содержимое:
 * гейт для `badges`, а не truthy результата вызова слота — см. JSDoc
 * `buildStatusBadges`
 * @param props.denseStatusSlot - плотный индикатор статуса (`StatusDot`) в
 * угловую группу `compact`, ТОЛЬКО широкая ветка (18976, Р77)
 * @param props.hasDenseStatus - есть ли у `denseStatusSlot` реальное
 * содержимое — угловой резерв и переключатель дот/пилюля должны знать об этом
 * СИНХРОННО с версткой, см. её JSDoc в `CardTypes.ts`
 * @param props.decisionSlot - пара решения («Отклонить»/«Одобрить», 18976,
 * Р77) — `decisionSlot` каркаса: в потоке ряда полей на `list`, абсолютный
 * нижний угол на `compact` (то же поведение, что уже несёт `CastingListRow`)
 * @param props.hasDecisionContent - есть ли у `decisionSlot` реальное
 * содержимое: тот же гейт, что у `hasStatusContent`, но для пары решения —
 * см. JSDoc `buildDecisionContent`
 * @param props.onQuickView - открыть окно быстрого просмотра вместо перехода на анкету
 */
export const CardListRow = <T extends IUserCardBase>({
  user,
  variant,
  sx = {},
  link,
  bottomContent,
  additionalInfo,
  metaSuffix,
  renderFolderAction,
  onBookmarked,
  onUnbookmarked,
  onQuickView,
  statusSlot,
  hasStatusContent = false,
  denseStatusSlot,
  hasDenseStatus = false,
  decisionSlot,
  hasDecisionContent = false
}: IProps<T>) => {
  const { shape } = useTheme();
  const {
    actorLink,
    folderIds,
    handleBookmarkClick,
    handleCardClick,
    handleFolderIdsChange,
    isBookmarked,
    isLoadingBookmarkUser
  } = useUserCardActions(user, link, {
    onBookmarked,
    onUnbookmarked,
    onQuickView
  });

  const isCompact = variant === 'compact';
  const frameContent = getFrameContent(
    user,
    isCompact,
    additionalInfo,
    metaSuffix
  );
  const hasCompactDot = isCompact && hasDenseStatus;
  const titleActionsSpacer = (
    <Box
      sx={getActionsReserveSx(!!renderFolderAction, hasCompactDot)}
      aria-hidden
    />
  );

  const badges = buildStatusBadges(
    hasStatusContent,
    isCompact,
    hasCompactDot,
    statusSlot
  );
  const decisionContent = buildDecisionContent(
    hasDecisionContent,
    decisionSlot
  );

  /**
   * Оборачивает содержимое строки (медиа/имя/поля) в ссылку на профиль — ссылка
   * остаётся вокруг ТОЛЬКО этого содержимого, не вокруг `footer`
   * (`bottomContent`): решение по отклику — интерактивные кнопки, вложенные в
   * `<a>` они вели бы себя непредсказуемо по клику.
   *
   * Ссылка блочная (19243): кольцо фокуса ложится по строке, а не по строчному
   * боксу; радиус — `borderL` поверхности строки (`getRowSurfaceSx`)
   * @param content - содержимое строки, которое рендерит каркас
   */
  const wrapWithLink = (content: ReactNode) => (
    <Box
      component={Link}
      to={user.visible ? actorLink : '#'}
      onClick={handleCardClick}
      sx={{
        display: 'block',
        textDecoration: 'none',
        color: 'inherit',
        '&:focus-visible': {
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: '2px',
          borderRadius: shape.borderL
        }
      }}
    >
      {content}
    </Box>
  );

  const trailing = buildTrailing(
    hasCompactDot,
    denseStatusSlot,
    renderFolderAction?.({
      targetId: Number(user.id),
      folderIds,
      isBookmarked,
      surface: 'light',
      onFolderIdsChange: handleFolderIdsChange
    }),
    <FavoriteHeartButton
      surface='light'
      isBookmarked={isBookmarked}
      onClick={handleBookmarkClick}
      isLoading={isLoadingBookmarkUser}
      sx={{ position: 'static' }}
    />
  );

  return (
    <Box sx={[getRowSurfaceSx, ROW_WRAPPER_SX, ...passedSx(sx)]}>
      <ListRowFrame
        variant={variant}
        surface={false}
        media={
          <UserAvatarWithPlaceholder
            // compact — 36×48, ему хватает варианта thumb 120×160; list — 84×112
            // (до 108×144 в широкой колонке), вариант card 480×640 (19244)
            photo={pickImageUrl(
              isCompact ? user.photo_thumb_url : user.photo_card_url,
              user.photo_card_url,
              user.photo
            )}
            gender={user.gender}
            styles={{
              borderRadius: shape.borderM,
              width: '100%',
              // Фон под ещё не загруженным фото — grey.200, как у постеров
              backgroundColor: 'grey.200'
            }}
          />
        }
        title={frameContent.title}
        titleActions={titleActionsSpacer}
        subtitle={frameContent.subtitle}
        subtitleMuted={isCompact}
        fields={frameContent.fields}
        meta={frameContent.meta}
        badges={badges}
        trailing={trailing}
        decisionSlot={decisionContent}
        contentWrapper={wrapWithLink}
        footer={buildRowFooter(bottomContent)}
      />
    </Box>
  );
};
